<!--
 * Path: src/features/browser/forms/markdown/MarkdownEdit.vue
 * Task: Dedicated editor area to write and preview markdown documentation using Vue 3.4+ defineModel and modular base tabs.
 * Update: Purged custom font-family overrides to ensure Vuetify typography compliance.
-->
<template>
  <v-card
    variant="outlined"
    class="markdown-editor"
  >
    <!-- BaseTabbar strictly handles the growth sizing -->
    <BaseTabbar
      v-model="tab"
      density="compact"
      bg-color="grey-lighten-5"
    >
      <BaseTab value="write">Write</BaseTab>
      <BaseTab value="preview">Preview</BaseTab>
    </BaseTabbar>

    <v-window v-model="tab">
      <v-window-item value="write">
        <v-textarea
          v-model="modelValue"
          :label="label"
          auto-grow
          clearable
          variant="filled"
          flat
          hide-details
          rows="5"
          class="text-body-1"
        ></v-textarea>
      </v-window-item>

      <v-window-item value="preview">
        <div
          class="markdown-preview pa-4 border-t text-body-1 text-on-surface"
          v-html="renderedMarkdown"
        ></div>
      </v-window-item>
    </v-window>
  </v-card>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import BaseTabbar from '@/core/components/common/baseTab/BaseTabbar.vue';
import BaseTab from '@/core/components/common/baseTab/BaseTab.vue';

interface Props {
  label?: string;
}

const props = withDefaults(defineProps<Props>(), {
  label: ''
});

const modelValue = defineModel<string>({ default: '' });

const tab = ref<string>('write');

const renderedMarkdown = computed<string>(() => {
  marked.setOptions({ gfm: true, breaks: true });
  const rawHtml = marked.parse(modelValue.value || `*${props.label || 'Content'} is empty.*`) as string;
  return DOMPurify.sanitize(rawHtml);
});
</script>

<style scoped>
.v-textarea {
  border-radius: 0;
}

/* Styles for BrowserView and internal preview */
.markdown-preview {
  line-height: 1.6;
}

.markdown-editor .markdown-preview {
  min-height: 140px;
}

.markdown-preview.border-t {
  border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

/* Markdown Element Styles */
.markdown-preview :deep(h1),
.markdown-preview :deep(h2),
.markdown-preview :deep(h3) {
  margin-top: 12px;
  margin-bottom: 8px;
  font-weight: 600;
  line-height: 1.25;
}

.markdown-preview :deep(h1) {
  font-size: 1.5em;
  border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  padding-bottom: 0.3em;
}

.markdown-preview :deep(h2) {
  font-size: 1.3em;
  border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  padding-bottom: 0.3em;
}

.markdown-preview :deep(h3) {
  font-size: 1.1em;
}

.markdown-preview :deep(ul),
.markdown-preview :deep(ol) {
  padding-left: 20px;
  margin-bottom: 12px;
}

.markdown-preview :deep(li) {
  margin-bottom: 4px;
}

.markdown-preview :deep(p) {
  margin-bottom: 12px;
}

.markdown-preview :deep(*:last-child) {
  margin-bottom: 0;
}

.markdown-preview :deep(a) {
  color: rgb(var(--v-theme-primary));
  text-decoration: none;
}

.markdown-preview :deep(a):hover {
  text-decoration: underline;
}

.markdown-preview :deep(blockquote) {
  border-left: 4px solid rgba(var(--v-border-color), var(--v-border-opacity));
  padding-left: 16px;
  margin-left: 0;
  color: rgba(var(--v-theme-on-surface), 0.6);
  font-style: italic;
}

.markdown-preview :deep(code) {
  background-color: rgba(var(--v-theme-on-surface), 0.05);
  padding: 2px 4px;
  border-radius: 3px;
  font-family: monospace;
  font-size: 0.9em;
}
</style>
